import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

export interface PageLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  maxWidth?: number | string;
  className?: string;
}

export function PageLayout({
  children,
  title,
  description,
  maxWidth = 896,
  className,
}: PageLayoutProps) {
  const maxWidthStyle = typeof maxWidth === 'number' ? { maxWidth: `${maxWidth}px` } : { maxWidth };

  return (
    <div
      className={cn('mx-auto flex w-full flex-col gap-6 px-4 py-6', className)}
      style={maxWidthStyle}>
      {title ? (
        <div className="flex flex-col gap-2">
          <h1 className="text-foreground text-3xl font-semibold tracking-tight">{title}</h1>
          {description ? <p className="text-muted-foreground text-base">{description}</p> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

export interface AuthLayoutProps {
  children: ReactNode;
  className?: string;
}

export function AuthLayout({ children, className }: AuthLayoutProps) {
  return (
    <div
      className={cn(
        'bg-background flex min-h-full w-full flex-1 items-center justify-center p-4',
        className
      )}>
      {children}
    </div>
  );
}

export interface FullBleedLayoutProps {
  children: ReactNode;
  className?: string;
}

export function FullBleedLayout({ children, className }: FullBleedLayoutProps) {
  return (
    <div
      className={cn(
        'bg-background flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden',
        className
      )}>
      {children}
    </div>
  );
}

export interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({ title, description, actions, className }: PageHeaderProps) {
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-3 md:flex-row md:items-start md:justify-between',
        className
      )}>
      <div className="flex flex-1 flex-col gap-1">
        <h1 className="text-foreground text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </div>
  );
}

export interface AppCardProps {
  children: ReactNode;
  className?: string;
}

export function AppCard({ children, className }: AppCardProps) {
  return (
    <div
      className={cn(
        'border-border bg-card text-card-foreground shadow-app rounded-xl border p-4',
        className
      )}>
      {children}
    </div>
  );
}

export interface AppSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  side?: 'left' | 'right';
  className?: string;
}

export function AppSheet({
  open,
  onOpenChange,
  children,
  side = 'right',
  className,
}: AppSheetProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/40"
        onClick={() => onOpenChange(false)}
      />
      <div
        className={cn(
          'border-border bg-background shadow-app-md absolute top-0 bottom-0 flex w-[min(100%,20rem)] flex-col p-4',
          side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
          className
        )}>
        {children}
      </div>
    </div>
  );
}
